/* eslint-disable react/no-danger */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import isObject from 'lodash/isObject';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { withRouter } from 'react-router';
import { withUrlGenerator } from '@folklore/react-app';
import Helmet from 'react-helmet';
import Player from '@vimeo/player';

import Blocks from '../blocks/Blocks';
import FilmInfo from '../blocks/FilmInfo';
import Slideshow from '../blocks/Slideshow';
import Embed from '../blocks/Embed';
import Background from '../partials/Background';
import BoxButton from '../partials/BoxButton';

import { setTrailerVisible as setTrailerVisibleActions } from '../../actions/LayoutActions';
import { setProductions as setProductionsActions } from '../../actions/SiteActions';

import styles from '../../../styles/pages/production.scss';

const propTypes = {
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    intl: PropTypes.shape({
        formatMessage: PropTypes.func,
    }).isRequired,
    urlGenerator: PropTypes.shape({
        route: PropTypes.func,
    }).isRequired,
    page: PropTypes.shape({
        data: PropTypes.shape({
            background: PropTypes.shape({
                type: PropTypes.oneOf(['video', 'image', 'color']),
                media: PropTypes.shape({
                    url: PropTypes.string,
                }),
            }),
            filmInfo: PropTypes.shape({
                director: PropTypes.string,
            }),
            blocks: PropTypes.array,
            slides: PropTypes.array,
        }),
    }),
    productions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number,
    })),
    trailerVisible: PropTypes.bool.isRequired,
    setProductions: PropTypes.func.isRequired,
    setTrailerVisible: PropTypes.func.isRequired,
};

const defaultProps = {
    page: null,
    productions: null,
};

class Production extends PureComponent {
    constructor(props) {
        super(props);

        this.onProductionsLoaded = this.onProductionsLoaded.bind(this);
        this.onClickWatchTrailer = this.onClickWatchTrailer.bind(this);
        this.onClickCloseTrailer = this.onClickCloseTrailer.bind(this);

        this.player = null;
        this.refEmbed = null;
    }

    componentDidMount() {
        const { productions, page } = this.props;
        if (productions === null && page === null) {
            this.fetchProductions();
        }
    }

    componentDidUpdate(prevProps) {
        const trailerVisibleChanged = prevProps.trailerVisible !== this.props.trailerVisible;
        if (trailerVisibleChanged) {
            if (this.props.trailerVisible) {
                this.initPlayer();
            } else {
                this.destroyPlayer();
            }
        }
    }

    componentWillUnmount() {
        if (this.player !== null) {
            this.player.unload();
            this.player = null;
        }
    }

    onProductionsLoaded(items) {
        this.props.setProductions(items);
    }

    onClickWatchTrailer() {
        this.props.setTrailerVisible(true);
    }

    onClickCloseTrailer() {
        if (this.player !== null) {
            this.player.unload();
        }
        this.props.setTrailerVisible(false);
    }

    fetchProductions() {
        const { urlGenerator } = this.props;
        fetch(urlGenerator.route('api.productions'))
            .then(response => response.json())
            .then(this.onProductionsLoaded);
    }

    initPlayer() {
        const iframe = get(this.refEmbed.getElementsByTagName('iframe'), 0, null);
        if (iframe === null) {
            return;
        }
        try {
            this.player = new Player(iframe);
            this.player.play();
        } catch (e) {
            this.player = null;
        }
    }

    destroyPlayer() {
        if (this.player !== null) {
            this.player = null;
        }
    }

    renderBackground(splashHeight) {
        const { width, page } = this.props;
        const background = get(page, 'background', null);
        if (background === null) {
            return null;
        }
        return (
            <div className={styles.background}>
                <Background {...background} width={width} height={splashHeight} current />
            </div>
        );
    }

    renderSplash(splashHeight) {
        const { intl, page } = this.props;
        const { locale } = intl;
        const awardsLogos = get(page, 'data.filmInfo.awardsLogos', []);
        const trailer = get(page, 'data.filmInfo.trailer', null);
        const hasTrailer = trailer && isObject(trailer) && trailer[`${locale}_embed`];
        const director = get(page, 'data.filmInfo.director', null);

        return (
            <Fragment>
                {this.renderBackground(splashHeight)}
                <div className={styles.gradient} />
                <div className={styles.label}>
                    <div className={styles.inner}>
                        <div className={styles.cols}>
                            <div
                                className={classNames({
                                    [styles.col]: true,
                                    [styles.colLabel]: true,
                                })}
                            >
                                <h1 className={styles.title}>{get(page, 'title', null)}</h1>
                                {director !== null ? (
                                    <div className={styles.director}>
                                        <FormattedMessage
                                            id="content.a_film_by"
                                            defaultMessage="A film by {name}"
                                            values={{
                                                name: director,
                                            }}
                                        />
                                    </div>
                                ) : null}

                                {hasTrailer ? (
                                    <div className={styles.actions}>
                                        <BoxButton
                                            label={{
                                                id: 'content.watch_trailer',
                                                defaultMessage: 'Watch trailer',
                                            }}
                                            onClick={this.onClickWatchTrailer}
                                        />
                                    </div>
                                ) : null}
                            </div>
                            <div
                                className={classNames({
                                    [styles.col]: true,
                                    [styles.colAwards]: true,
                                })}
                            >
                                <div className={styles.awards}>
                                    <ul className={styles.items}>
                                        {awardsLogos.map(it => (
                                            <li className={styles.item}>
                                                <img
                                                    src={it.image.url}
                                                    alt={get(it, `label.${locale}`, '')}
                                                    title={get(it, `label.${locale}`, '')}
                                                    className={styles.image}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }

    render() {
        const {
            intl, width, height, page, trailerVisible,
        } = this.props;
        const { locale } = intl;

        // prettier-ignore
        const splashHeight = width > height ? (height - (height * 0.1)) : (height - (height * 0.3));
        const splashStyle = {
            height: splashHeight,
        };

        const filmInfo = get(page, 'data.filmInfo', null);
        const trailer = get(page, 'data.filmInfo.trailer', null);
        const imageSlides = get(page, 'data.imageSlides', null);

        const slides =
            imageSlides !== null && imageSlides.length > 0
                ? imageSlides.map(slide => ({
                    page: {
                        background: {
                            type: 'image',
                            image: get(slide, 'picture', null),
                        },
                        description: get(slide, 'description', null),
                    },
                }))
                : null;

        const hasTrailer =
            get(
                page,
                `data.filmInfo.trailer.${locale}_embed.html`,
                get(
                    page,
                    'data.filmInfo.trailer.en_embed.html',
                    get(page, 'data.filmInfo.trailer.fr_embed.html', null),
                ),
            ) !== null;

        return (
            <div className={styles.container}>
                <Helmet>
                    <title>
                        {intl.formatMessage(
                            {
                                id: 'meta.title_production',
                                defaultMessage: 'Accueil',
                            },
                            {
                                title: get(page, 'title', ''),
                            },
                        )}
                    </title>
                </Helmet>
                <div
                    className={classNames({
                        [styles.section]: true,
                        [styles.splash]: true,
                    })}
                    style={splashStyle}
                >
                    {this.renderSplash(splashHeight)}
                </div>
                <div
                    className={classNames({
                        [styles.section]: true,
                        [styles.content]: true,
                    })}
                >
                    <div className={styles.filmInfo}>
                        <FilmInfo {...filmInfo} url={get(page, 'url', '')} />
                    </div>
                    <div className={styles.blocks}>
                        <Blocks
                            items={get(page, 'data.blocks', []).map((block, index) => ({
                                ...block,
                                first: index === 0,
                            }))}
                        />
                    </div>
                </div>
                {hasTrailer !== null ? (
                    <div
                        className={classNames({
                            [styles.section]: true,
                            [styles.trailer]: true,
                            [styles.visible]: trailerVisible,
                        })}
                    >
                        <button
                            type="button"
                            className={styles.close}
                            onClick={this.onClickCloseTrailer}
                        />
                        <div className={styles.inner}>
                            <div className={styles.middle}>
                                <Embed
                                    url={trailer}
                                    embedRef={(ref) => {
                                        this.refEmbed = ref;
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                ) : null}
                {slides !== null && slides.length > 0 ? (
                    <div
                        className={classNames({
                            [styles.section]: true,
                            [styles.slideshow]: true,
                        })}
                    >
                        <div
                            className={classNames({
                                [styles.slideshowContainer]: true,
                            })}
                        >
                            <div
                                className={classNames({
                                    [styles.slideshowInner]: true,
                                })}
                            >
                                <Slideshow slides={slides} lowerNav arrowNav />
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }
}

Production.propTypes = propTypes;
Production.defaultProps = defaultProps;

const mapStateToProps = ({ site, layout }, { location }) => ({
    ...layout.size,
    productions: site.productions,
    trailerVisible: layout.trailerVisible,
    page:
        site.page !== null && site.page.url === location.pathname
            ? site.page
            : (site.productions || []).find(it => it.url === location.pathname) || null,
});
const mapDispatchToProps = dispatch => ({
    setProductions: items => dispatch(setProductionsActions(items)),
    setTrailerVisible: items => dispatch(setTrailerVisibleActions(items)),
});
const WithStateContainer = connect(mapStateToProps, mapDispatchToProps)(Production);
const WithIntlContainer = injectIntl(WithStateContainer);
const WithRouterContainer = withRouter(WithIntlContainer);
const WithUrlGeneratorContainer = withUrlGenerator()(WithRouterContainer);

export default WithUrlGeneratorContainer;



// WEBPACK FOOTER //
// ./js/components/pages/Production.jsx